<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Auth from 'dashboard/api/auth';
import SearchAPI from 'dashboard/api/search';
import SzNewMessageModal from './SzNewMessageModal.vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useTemplateRef } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { formatChatListDate } from 'shared/helpers/timeHelper';

const store = useStore();
const router = useRouter();
const route = useRoute();

// SZ unify: quando estamos na rota /search, este TopBar dirige a busca da página
const isOnSearchRoute = computed(() => route.name === 'search');

const searchQuery = ref('');
const showNewMsgModal = ref(false);
// SZ Inteligência button: 2-stage Lottie com 2 containers sobrepostos.
// Ambos os lotties (in-out + loop) ficam pré-carregados, sempre montados.
// A troca entre estados só muda qual layer está visível — evita o flash que
// acontecia ao destroy/recriar lottie no mesmo container.
//
// Estados:
//   idle     → layer in-out visível, frame 0
//   entering → layer in-out visível, tocando forward
//   looping  → layer loop visível, tocando loop infinito
//   leaving  → layer in-out visível, tocando reverse
const iaLottieContainer     = ref(null); // in-out layer
const iaLottieLoopContainer = ref(null); // loop layer
let iaLottieAnim     = null; // in-out animation
let iaLottieLoopAnim = null; // loop animation
let iaLottieDarkState = null;
let iaState = 'idle';

const IA_IN_OUT_PATH_DARK  = '/static/img/sz-ia-button-in-out-dark.json?v=20260525a';
const IA_IN_OUT_PATH_LIGHT = '/static/img/sz-ia-button-in-out-light.json?v=20260525a';
const IA_LOOP_PATH         = '/static/img/sz-ia-button-loop.json?v=20260525a';
// Multiplicador de velocidade. 1 = normal; 1.6 = 60% mais rápido. Aplicado a
// ambos in-out e loop. Ajuste em 1 lugar.
const IA_PLAYBACK_SPEED    = 1.6;

function toggleIntelligence() {
  window.dispatchEvent(new CustomEvent('sz-toggle-intelligence'));
}

function _iaApplySvgSize(el) {
  if (!el) return;
  const svg = el.querySelector('svg');
  if (svg) { svg.style.width = '38px'; svg.style.height = '38px'; }
}

function _iaShowLayer(which) {
  // 'inout' | 'loop' — cross-fade via opacity (CSS transition: 150ms linear)
  if (iaLottieContainer.value)     iaLottieContainer.value.style.opacity     = (which === 'inout') ? '1' : '0';
  if (iaLottieLoopContainer.value) iaLottieLoopContainer.value.style.opacity = (which === 'loop')  ? '1' : '0';
}

function _iaOnInOutComplete() {
  if (iaState === 'entering') {
    iaState = 'looping';
    if (iaLottieLoopAnim) {
      iaLottieLoopAnim.goToAndStop(0, true);
      iaLottieLoopAnim.play();
    }
    _iaShowLayer('loop');
  } else if (iaState === 'leaving') {
    iaState = 'idle';
    if (iaLottieAnim) iaLottieAnim.goToAndStop(0, true);
  }
}

function _iaLoadInOut(isDark) {
  return new Promise(async (resolve) => {
    const el = iaLottieContainer.value;
    if (!el || !window.lottie) { resolve(); return; }
    if (iaLottieAnim) { try { iaLottieAnim.destroy(); } catch (e) {} iaLottieAnim = null; }
    el.innerHTML = '';
    try {
      const url = isDark ? IA_IN_OUT_PATH_DARK : IA_IN_OUT_PATH_LIGHT;
      const resp = await fetch(url);
      const data = await resp.json();
      iaLottieAnim = window.lottie.loadAnimation({
        container: el, renderer: 'svg', loop: false, autoplay: false, animationData: data,
      });
      iaLottieAnim.setSpeed(IA_PLAYBACK_SPEED);
      iaLottieDarkState = isDark;
      iaLottieAnim.addEventListener('DOMLoaded', () => { _iaApplySvgSize(el); resolve(); });
      iaLottieAnim.addEventListener('complete', _iaOnInOutComplete);
    } catch (e) { resolve(); }
  });
}

function _iaLoadLoop() {
  return new Promise(async (resolve) => {
    const el = iaLottieLoopContainer.value;
    if (!el || !window.lottie) { resolve(); return; }
    if (iaLottieLoopAnim) { try { iaLottieLoopAnim.destroy(); } catch (e) {} iaLottieLoopAnim = null; }
    el.innerHTML = '';
    try {
      const resp = await fetch(IA_LOOP_PATH);
      const data = await resp.json();
      iaLottieLoopAnim = window.lottie.loadAnimation({
        container: el, renderer: 'svg', loop: true, autoplay: false, animationData: data,
      });
      iaLottieLoopAnim.setSpeed(IA_PLAYBACK_SPEED);
      iaLottieLoopAnim.addEventListener('DOMLoaded', () => { _iaApplySvgSize(el); resolve(); });
    } catch (e) { resolve(); }
  });
}

function iaLottiePlay() {
  // mouseenter
  if (iaState === 'idle' && iaLottieAnim) {
    iaState = 'entering';
    _iaShowLayer('inout');
    iaLottieAnim.setDirection(1);
    iaLottieAnim.play();
  } else if (iaState === 'leaving' && iaLottieAnim) {
    iaState = 'entering';
    iaLottieAnim.setDirection(1);
    iaLottieAnim.play();
  }
}

function iaLottiePause() {
  // mouseleave
  if (iaState === 'entering' && iaLottieAnim) {
    iaState = 'leaving';
    iaLottieAnim.setDirection(-1);
    iaLottieAnim.play();
  } else if (iaState === 'looping' && iaLottieAnim) {
    // troca instantânea de layer: pausa loop, mostra in-out já carregado
    // e toca reverse a partir do último frame.
    iaState = 'leaving';
    if (iaLottieLoopAnim) iaLottieLoopAnim.pause();
    const lastFrame = (iaLottieAnim.totalFrames || 1) - 1;
    iaLottieAnim.goToAndStop(lastFrame, true);
    _iaShowLayer('inout');
    iaLottieAnim.setDirection(-1);
    iaLottieAnim.play();
  }
}

async function loadIaLottie(isDark) {
  iaState = 'idle';
  await Promise.all([_iaLoadInOut(isDark), _iaLoadLoop()]);
  if (iaLottieAnim) iaLottieAnim.goToAndStop(0, true);
  _iaShowLayer('inout');
}
const showSearchDropdown = ref(false);
const searchLoading = ref(false);
const searchResults = ref({ contacts: [], conversations: [], messages: [] });
const SEARCH_FILTERS = [
  { id: 'all',           label: 'Tudo' },
  { id: 'mine',          label: 'Atribuídas a mim' },
  { id: 'unassigned',    label: 'Sem atribuição' },
  { id: 'conversations', label: 'Conversas' },
  { id: 'groups',        label: 'Grupos' },
  { id: 'messages',      label: 'Mensagens' },
  { id: 'contacts',      label: 'Contatos' },
];
const SEARCH_FILTER_KEY = 'sz_search_filter';
const _initialFilter = (() => {
  try {
    const v = localStorage.getItem(SEARCH_FILTER_KEY);
    return SEARCH_FILTERS.some(f => f.id === v) ? v : 'all';
  } catch (e) { return 'all'; }
})();
const searchFilter = ref(_initialFilter);
function setSearchFilter(id) {
  searchFilter.value = id;
  try { localStorage.setItem(SEARCH_FILTER_KEY, id); } catch (e) {}
}
function isGroupContact(c) {
  if (!c) return false;
  const id = c.identifier || '';
  if (id.endsWith('@g.us')) return true;
  const name = c.name || '';
  return /\(grupo\)/i.test(name) || /\(group\)/i.test(name);
}
const filteredSearchResults = computed(() => {
  const f = searchFilter.value;
  const r = searchResults.value;
  if (f === 'all') return r;
  if (f === 'contacts')      return { contacts: r.contacts,      conversations: [], messages: [] };
  if (f === 'messages')      return { contacts: [],              conversations: [], messages: r.messages };
  if (f === 'conversations') return { contacts: [],              conversations: r.conversations, messages: [] };
  if (f === 'groups') {
    const groups = (r.conversations || []).filter(c => {
      const sender = c.meta && c.meta.sender;
      return isGroupContact(sender) || isGroupContact(c.contact);
    });
    return { contacts: [], conversations: groups, messages: [] };
  }
  if (f === 'mine') {
    const uid = currentUser.value?.id;
    const mine = (r.conversations || []).filter(c => {
      const a = c.meta && c.meta.assignee;
      return a && Number(a.id) === Number(uid);
    });
    return { contacts: [], conversations: mine, messages: [] };
  }
  if (f === 'unassigned') {
    const un = (r.conversations || []).filter(c => {
      const a = c.meta && c.meta.assignee;
      return !a || !a.id;
    });
    return { contacts: [], conversations: un, messages: [] };
  }
  return r;
});
const searchDebounce = ref(null);
const recentSearches = ref([]);
const showStatusDropdown = ref(false);
const showProductsDropdown = ref(false);
const showProfileDropdown = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const currentUserAvailability = computed(() => store.getters.getCurrentUserAvailability);
// SZ: modo automatico de presenca — quando true, status efetivo segue janela aberta/fechada (Chatwoot nativo via auto_offline)
const currentUserAutoOffline = computed(() => store.getters.getCurrentUserAutoOffline);
const autoOfflineEnabled = computed(() => currentUserAutoOffline.value !== false);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const userName = computed(() => currentUser.value?.name || '');
// SZ_USERNAME_SHORT_20260528: limita o nome exibido na top bar a 2 nomes
// + reticências, com hard-cap de caracteres pra nomes "colados" gigantes,
// evitando empurrar o resto da barra. Nome completo fica no title/dropdown.
const userNameShort = computed(() => {
  const full = (userName.value || '').trim();
  if (!full) return '';
  const parts = full.split(/\s+/).filter(Boolean);
  // Formato "Primeiro X." — primeiro nome + inicial do segundo + ponto.
  let out;
  if (parts.length >= 2) {
    out = parts[0] + ' ' + parts[1].charAt(0).toUpperCase() + '.';
  } else {
    out = parts[0] || full;
  }
  // Se ainda passar muito (primeiro nome gigante ou token colado), reticências.
  if (out.length > 18) out = out.slice(0, 18).trimEnd() + '…';
  return out;
});
const userAvatar = computed(() => currentUser.value?.avatar_url || currentUser.value?.thumbnail || '');
const userInitials = computed(() => {
  const n = userName.value;
  if (!n) return '?';
  return n.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
});
const userStatus = computed(() => currentUserAvailability.value || 'online');
const statusLabel = computed(() => {
  const map = { online: 'Ativo', busy: 'Ocupado', offline: 'Offline' };
  return map[userStatus.value] || 'Ativo';
});
const statusColor = computed(() => {
  const map = { online: '#7CCF00', busy: '#F59E0B', offline: '#9CA3AF' };
  return map[userStatus.value] || '#7CCF00';
});

const SZ_MODULES = [
  { name: 'Chat', color: '#388E3C', icon: '/static/img/Chat.png', active: true },
  { name: 'Monitoramento', color: '#BF6430', icon: '/static/img/monitoramento.png', active: false },
  { name: 'CRM', color: '#2E6CC4', icon: '/static/img/CRM.png', active: false },
  { name: 'Pay', color: '#F57D00', icon: '/static/img/Pay.png', active: false },
  { name: 'Teams', color: '#AD46FF', icon: '/static/img/Teams.png', active: false },
];

const isSettingsPage = computed(() => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  return path.includes('/settings');
});

function onSearchFocus() {
  showSearchDropdown.value = true;
  showStatusDropdown.value = false;
  showProductsDropdown.value = false;
  showProfileDropdown.value = false;
  loadRecentSearches();
}
function onSearchInput() {
  if (searchDebounce.value) clearTimeout(searchDebounce.value);
  // SZ unify: na rota /search, este input dirige a página geral em vez do dropdown
  if (isOnSearchRoute.value) {
    showSearchDropdown.value = false;
    searchDebounce.value = setTimeout(() => {
      emitter.emit('sz:search-query', searchQuery.value);
      const q = (searchQuery.value || '').trim();
      const target = q ? { ...route.query, q } : { ...route.query };
      if (!q) delete target.q;
      router.replace({ name: 'search', params: route.params, query: target }).catch(() => {});
    }, 350);
    return;
  }
  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchResults.value = { contacts: [], conversations: [], messages: [] };
    searchLoading.value = false;
    return;
  }
  searchLoading.value = true;
  searchDebounce.value = setTimeout(() => doSearch(searchQuery.value), 400);
}
async function doSearch(query) {
  try {
    const resp = await SearchAPI.get({ q: query });
    const data = resp.data?.payload || resp.data || {};
    searchResults.value = {
      contacts: (data.contacts || []).slice(0, 5),
      conversations: (data.conversations || []).slice(0, 5),
      messages: (data.messages || []).slice(0, 5),
    };
    saveRecentSearch(query);
  } catch {
    searchResults.value = { contacts: [], conversations: [], messages: [] };
  }
  searchLoading.value = false;
}
function getInboxNameById(inboxId) {
  if (!inboxId) return '';
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  return inbox?.name || '';
}
function getInboxName(conv) {
  if (!conv) return '';
  // Prefer the actual inbox name from the store (e.g. "RH Principal")
  if (conv.inbox_id) {
    const fromStore = getInboxNameById(conv.inbox_id);
    if (fromStore) return fromStore;
  }
  if (conv.inbox?.name) return conv.inbox.name;
  // Fallback: channel type ("Channel::Api" → "Api") so something is shown
  if (conv.meta?.channel) return conv.meta.channel.replace('Channel::', '');
  return '';
}
function closeSearchDropdown() {
  setTimeout(() => { showSearchDropdown.value = false; }, 200);
}
function goToConversation(convId) {
  showSearchDropdown.value = false;
  searchQuery.value = '';
  router.push(`/app/accounts/${accountId.value}/conversations/${convId}`).catch(() => {});
}
function goToContact(contactId) {
  showSearchDropdown.value = false;
  searchQuery.value = '';
  router.push(`/app/accounts/${accountId.value}/contacts/${contactId}`).catch(() => {});
}
function goToFullSearch() {
  showSearchDropdown.value = false;
  router.push({ name: 'search', params: { accountId: accountId.value }, query: { q: searchQuery.value } }).catch(() => {});
}
function loadRecentSearches() {
  try {
    const stored = localStorage.getItem('sz_recent_searches');
    recentSearches.value = stored ? JSON.parse(stored) : [];
  } catch { recentSearches.value = []; }
}
function saveRecentSearch(query) {
  try {
    let list = recentSearches.value.filter(q => q !== query);
    list.unshift(query);
    if (list.length > 5) list = list.slice(0, 5);
    recentSearches.value = list;
    localStorage.setItem('sz_recent_searches', JSON.stringify(list));
  } catch {}
}
function searchRecent(query) {
  searchQuery.value = query;
  onSearchInput();
}
function clearRecent() {
  recentSearches.value = [];
  localStorage.removeItem('sz_recent_searches');
}
function toggleProductsDropdown() {
  showProductsDropdown.value = !showProductsDropdown.value;
  showProfileDropdown.value = false;
  showStatusDropdown.value = false;
}
function toggleProfileDropdown() {
  showProfileDropdown.value = !showProfileDropdown.value;
  showProductsDropdown.value = false;
  showStatusDropdown.value = false;
}
function goToProfile() {
  showProfileDropdown.value = false;
  router.push({ name: 'profile_settings_index' }).catch(() => {});
}
function goToAppearance() {
  showProfileDropdown.value = false;
  const ninja = document.querySelector('ninja-keys');
  if (ninja) ninja.open({ parent: 'appearance_settings' });
}
function goToKeyboardShortcuts() {
  showProfileDropdown.value = false;
  const ninja = document.querySelector('ninja-keys');
  if (ninja) ninja.open({ parent: 'shortcut_settings' });
}
function goToSuperAdmin() {
  showProfileDropdown.value = false;
  window.open('/super_admin', '_blank');
}
function openHelp() { window.open('https://ajuda.solarzdashboard.com.br', '_blank'); }
function doLogout() {
  Auth.logout();
}
// ComposeConversation manages its own open/close state via slot trigger
function setStatus(status) {
  store.dispatch('updateAvailability', { availability: status, account_id: accountId.value });
  showStatusDropdown.value = false;
}
// SZ: alterna modo automatico (auto_offline) — POST /api/v1/profile/auto_offline
function toggleAutoOffline(event) {
  const newValue = event.target.checked;
  store.dispatch('updateAutoOffline', { accountId: accountId.value, autoOffline: newValue });
}
// SZ: quando modo Auto esta ON, aba ativa = online, aba oculta/sem foco = offline.
// Considera document.visibilityState (alt-tab/minimizar) E document.hasFocus (blur sem trocar de aba).
// Preserva 'Ocupado' (escolha manual nao deve ser sobrescrita pela visibilidade).
function handleVisibilityChange() {
  if (!autoOfflineEnabled.value) return;
  const current = userStatus.value;
  if (current === 'busy') return;
  const isActive = document.visibilityState === 'visible' && document.hasFocus();
  if (!isActive && current === 'online') {
    store.dispatch('updateAvailability', { availability: 'offline', account_id: accountId.value });
  } else if (isActive && current === 'offline') {
    store.dispatch('updateAvailability', { availability: 'online', account_id: accountId.value });
  }
}
// SZ: dispara reconciliacao quando o toggle Automatico muda de OFF -> ON
// (se a aba esta visivel e status atual = offline, vira online imediatamente)
watch(autoOfflineEnabled, (isOn, wasOn) => {
  if (isOn && !wasOn) handleVisibilityChange();
});
// SZ: reconciliacao no primeiro carregamento do dado do user.
// userStatus comeca como 'online' (fallback) ate currentUserAvailability popular.
// Sem isso, no mount visibility=visible+current='online' (fallback) nao dispara nada
// e o status fica preso no que o backend retornar (ex: offline gravado).
const stopAvailWatch = watch(currentUserAvailability, (val) => {
  if (val) {
    handleVisibilityChange();
    stopAvailWatch();
  }
}, { immediate: true });
function toggleStatusDropdown() {
  showStatusDropdown.value = !showStatusDropdown.value;
  showProductsDropdown.value = false;
  showProfileDropdown.value = false;
}
function handleClickOutside(e) {
  if (showStatusDropdown.value && !e.target.closest('.sz-status-pill')) {
    showStatusDropdown.value = false;
  }
  if (showProductsDropdown.value && !e.target.closest('.sz-products-wrap')) {
    showProductsDropdown.value = false;
  }
  if (showProfileDropdown.value && !e.target.closest('.sz-profile-pill')) {
    showProfileDropdown.value = false;
  }
  if (showSearchDropdown.value && !e.target.closest('.sz-search-wrap')) {
    showSearchDropdown.value = false;
  }
}
// SZ unify: handler que recebe da SearchView a query inicial da URL ao montar
function onSzSearchViewMounted(q) {
  searchQuery.value = q || '';
}
function onSzSearchViewUnmounted() {
  // ao sair da rota search, limpa o input do header para não vazar query
  searchQuery.value = '';
  showSearchDropdown.value = false;
}

// pré-popula quando entra/atualiza a rota search
watch(
  () => [route.name, route.query?.q],
  ([name, q]) => {
    if (name === 'search') {
      const next = (q || '').toString();
      if (next !== searchQuery.value) searchQuery.value = next;
    }
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  emitter.on('sz:search-view-mounted', onSzSearchViewMounted);
  emitter.on('sz:search-view-unmounted', onSzSearchViewUnmounted);
  // SZ: listener de visibilidade da aba para modo Auto + focus/blur como fallback
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleVisibilityChange);
  window.addEventListener('blur', handleVisibilityChange);
  // dispara uma vez no mount: se aba visivel + auto on + status offline, vira online
  handleVisibilityChange();
  // Load lottie-web library (needed for IA icon)
  if (!window.lottie) {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    document.head.appendChild(s);
    s.onload = () => {
      loadIaLottie(document.body.classList.contains('dark'));
    };
  } else {
    loadIaLottie(document.body.classList.contains('dark'));
  }

  // Watch for dark mode changes
  const lottieDarkObs = new MutationObserver(() => {
    const isDark = document.body.classList.contains('dark');

    if (isDark !== iaLottieDarkState) loadIaLottie(isDark);
  });
  lottieDarkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
});
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleVisibilityChange);
  window.removeEventListener('blur', handleVisibilityChange);
  emitter.off('sz:search-view-mounted', onSzSearchViewMounted);
  emitter.off('sz:search-view-unmounted', onSzSearchViewUnmounted);
  if (typeof lottieDarkObs !== 'undefined') lottieDarkObs.disconnect();
});
</script>

<template>
  <div class="sz-header">
    <div class="sz-header-container">
      <!-- LEFT: Logo + Chat -->
      <div class="sz-header-left" style="cursor:pointer;" @click="router.push(`/app/accounts/${accountId}/dashboard`)">
        <img :src="'/static/img/sz-logo-chat.svg'" alt="SolarZ" class="sz-header-logo-img" style="width:40px;height:40px;" />
        <span class="sz-header-logo-text">SolarZ Chat</span>
      </div>

      <!-- CENTER: Search + New Message -->
      <div class="sz-header-center">
        <div class="sz-search-wrap" style="position:relative;flex:1;">
          <div class="sz-search-field">
            <svg class="sz-search-icon" width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M17.25 17.25L20.75 20.75M19.75 10.25C19.75 15.4967 15.4967 19.75 10.25 19.75C5.00329 19.75 0.75 15.4967 0.75 10.25C0.75 5.00329 5.00329 0.75 10.25 0.75C15.4967 0.75 19.75 5.00329 19.75 10.25Z" stroke="#D4D4D4" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              class="sz-search-input"
              placeholder="Pesquisar conversas e contatos"
              @focus="onSearchFocus"
              @input="onSearchInput"
              @blur="closeSearchDropdown"
              @keydown.enter="goToFullSearch"
            />
          </div>
          <!-- Search Dropdown -->
          <div v-if="showSearchDropdown" class="sz-search-dropdown">
            <!-- Filter chips (always visible while dropdown open) -->
            <div class="sz-search-filters-bar">
              <button
                v-for="f in SEARCH_FILTERS"
                :key="f.id"
                type="button"
                class="sz-search-filter-chip"
                :class="{ active: searchFilter === f.id }"
                @mousedown.prevent="setSearchFilter(f.id)"
              >
                <span class="sz-search-filter-chip-check" :class="{ 'sz-search-filter-chip-check--on': searchFilter === f.id }">
                  <svg v-if="searchFilter === f.id" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span class="sz-search-filter-chip-label">{{ f.label }}</span>
              </button>
            </div>
            <!-- Loading -->
            <div v-if="searchLoading" class="sz-search-loading">
              <span>Buscando...</span>
            </div>
            <!-- No query - show recents -->
            <template v-else-if="!searchQuery || searchQuery.length < 2">
              <div v-if="recentSearches.length" class="sz-search-section">
                <div class="sz-search-section-header">
                  <span>Buscas recentes</span>
                  <button class="sz-search-clear" @mousedown.prevent="clearRecent">Limpar</button>
                </div>
                <div
                  v-for="q in recentSearches"
                  :key="q"
                  class="sz-search-item"
                  @mousedown.prevent="searchRecent(q)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>{{ q }}</span>
                </div>
              </div>
              <div v-else class="sz-search-empty">Digite para buscar contatos e conversas</div>
            </template>
            <!-- Results -->
            <template v-else>
              <!-- Conversations -->
              <div v-if="filteredSearchResults.conversations.length" class="sz-search-section">
                <div class="sz-search-section-header"><span>Conversas</span></div>
                <div
                  v-for="conv in filteredSearchResults.conversations"
                  :key="'v'+conv.id"
                  class="sz-search-item"
                  @mousedown.prevent="goToConversation(conv.id)"
                >
                  <div class="sz-search-avatar">
                    <img v-if="conv.meta?.sender?.thumbnail" :src="conv.meta.sender.thumbnail" />
                    <span v-else>{{ (conv.meta?.sender?.name || '#')[0].toUpperCase() }}</span>
                  </div>
                  <div class="sz-search-item-text">
                    <span class="sz-search-item-name">{{ conv.meta?.sender?.name || conv.contact?.name || 'Conversa #' + conv.id }}</span>
                    <span class="sz-search-item-meta">
                      {{ conv.meta?.sender?.phone_number || conv.contact?.phone_number || '' }}{{ conv.messages?.[0]?.content ? ' · ' + conv.messages[0].content.substring(0, 50) : '' }}
                    </span>
                  </div>
                  <!-- SZ: data da última atividade — badge cinza alinhado ao lado do inbox tag -->
                  <span
                    v-if="conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt"
                    class="sz-search-item-date"
                  >{{ formatChatListDate(conv.last_activity_at || conv.lastActivityAt || conv.created_at || conv.createdAt) }}</span>
                  <span v-if="getInboxName(conv)" class="sz-search-inbox-tag">{{ getInboxName(conv) }}</span>
                </div>
              </div>
              <!-- Messages -->
              <div v-if="filteredSearchResults.messages.length" class="sz-search-section">
                <div class="sz-search-section-header"><span>Mensagens</span></div>
                <div
                  v-for="msg in filteredSearchResults.messages"
                  :key="'m'+msg.id"
                  class="sz-search-item"
                  @mousedown.prevent="goToConversation(msg.conversation_id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 9h8M8 13h6"/></svg>
                  <div class="sz-search-item-text">
                    <span class="sz-search-item-name">{{ msg.content?.substring(0, 80) || '' }}</span>
                    <span class="sz-search-item-meta">Conversa #{{ msg.conversation_id }}</span>
                  </div>
                  <span v-if="getInboxNameById(msg.inbox_id)" class="sz-search-inbox-tag">{{ getInboxNameById(msg.inbox_id) }}</span>
                </div>
              </div>
              <!-- Contacts -->
              <div v-if="filteredSearchResults.contacts.length" class="sz-search-section">
                <div class="sz-search-section-header"><span>Contatos</span></div>
                <div
                  v-for="contact in filteredSearchResults.contacts"
                  :key="'c'+contact.id"
                  class="sz-search-item"
                  @mousedown.prevent="goToContact(contact.id)"
                >
                  <div class="sz-search-avatar">
                    <img v-if="contact.thumbnail" :src="contact.thumbnail" />
                    <span v-else>{{ (contact.name || '?')[0].toUpperCase() }}</span>
                  </div>
                  <div class="sz-search-item-text">
                    <span class="sz-search-item-name">{{ contact.name || 'Sem nome' }}</span>
                    <span class="sz-search-item-meta">{{ contact.phone_number || contact.email || '' }}</span>
                  </div>
                </div>
              </div>
              <!-- No results -->
              <div v-if="!filteredSearchResults.contacts.length && !filteredSearchResults.conversations.length && !filteredSearchResults.messages.length" class="sz-search-empty">
                Nenhum resultado para "{{ searchQuery }}"
              </div>
              <!-- Full search link -->
              <div class="sz-search-footer" @mousedown.prevent="goToFullSearch">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <span>Ver todos os resultados</span>
              </div>
            </template>
          </div>
        </div>
        <button class="sz-new-msg-btn" title="Nova mensagem" @click="showNewMsgModal = true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2.5V17.5M2.5 10H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </button>
        <SzNewMessageModal v-if="showNewMsgModal" @close="showNewMsgModal = false" />
      </div>

      <!-- RIGHT: Status + Icons + Profile -->
      <div class="sz-header-right">
        <!-- Status pill -->
        <div class="sz-status-pill" @click="toggleStatusDropdown">
          <div class="sz-status-dot" :style="{ background: statusColor }" />
          <span class="sz-status-label">{{ statusLabel }}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <!-- Status dropdown -->
          <div v-if="showStatusDropdown" class="sz-status-dropdown" @click.stop>
            <div class="sz-status-option" @click="setStatus('online')">
              <div class="sz-status-dot" style="background: #7CCF00" />
              <span>Ativo</span>
              <svg v-if="userStatus === 'online'" class="sz-status-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div class="sz-status-option" @click="setStatus('busy')">
              <div class="sz-status-dot" style="background: #F59E0B" />
              <span>Ocupado</span>
              <svg v-if="userStatus === 'busy'" class="sz-status-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div class="sz-status-option" @click="setStatus('offline')">
              <div class="sz-status-dot" style="background: #9CA3AF" />
              <span>Offline</span>
              <svg v-if="userStatus === 'offline'" class="sz-status-check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <!-- SZ: toggle automatico (Chatwoot auto_offline) — default ON, alterna manual<>auto -->
            <div class="sz-status-divider"></div>
            <label class="sz-status-auto-row" @click.stop>
              <div class="sz-status-auto-toprow">
                <span class="sz-status-auto-switch" :class="{ 'is-on': autoOfflineEnabled }">
                  <input type="checkbox" :checked="autoOfflineEnabled" @change="toggleAutoOffline" />
                  <span class="sz-status-auto-slider"></span>
                </span>
                <span class="sz-status-auto-label">Automático</span>
              </div>
              <span class="sz-status-auto-help">Status troca ao mudar de aba</span>
            </label>
          </div>
        </div>

        <!-- Icon buttons -->
        <div class="sz-icon-group">
          <!-- AI / Notificações Lottie (2 layers sobrepostos: in-out + loop) -->
          <div
            class="sz-ia-lottie-wrap"
            title="SZ Inteligência"
            @click="toggleIntelligence"
            @mouseenter="iaLottiePlay"
            @mouseleave="iaLottiePause"
          >
            <div ref="iaLottieContainer" class="sz-ia-lottie-layer" style="opacity:1" />
            <div ref="iaLottieLoopContainer" class="sz-ia-lottie-layer" style="opacity:0" />
          </div>
          <!-- Help -->
          <button class="sz-icon-btn" title="Ajuda" @click="openHelp">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10.9375 14.0625C10.9375 14.2479 10.8825 14.4292 10.7795 14.5833C10.6765 14.7375 10.5301 14.8577 10.3588 14.9286C10.1875 14.9996 9.99896 15.0182 9.81711 14.982C9.63525 14.9458 9.4682 14.8565 9.33709 14.7254C9.20598 14.5943 9.11669 14.4273 9.08052 14.2454C9.04434 14.0635 9.06291 13.875 9.13387 13.7037C9.20482 13.5324 9.32499 13.386 9.47916 13.283C9.63333 13.18 9.81458 13.125 10 13.125C10.2486 13.125 10.4871 13.2238 10.6629 13.3996C10.8387 13.5754 10.9375 13.8139 10.9375 14.0625ZM10 5.625C8.27657 5.625 6.875 6.88672 6.875 8.4375V8.75C6.875 8.91576 6.94085 9.07473 7.05806 9.19194C7.17527 9.30915 7.33424 9.375 7.5 9.375C7.66576 9.375 7.82474 9.30915 7.94195 9.19194C8.05916 9.07473 8.125 8.91576 8.125 8.75V8.4375C8.125 7.57812 8.96641 6.875 10 6.875C11.0336 6.875 11.875 7.57812 11.875 8.4375C11.875 9.29688 11.0336 10 10 10C9.83424 10 9.67527 10.0658 9.55806 10.1831C9.44085 10.3003 9.375 10.4592 9.375 10.625V11.25C9.375 11.4158 9.44085 11.5747 9.55806 11.6919C9.67527 11.8092 9.83424 11.875 10 11.875C10.1658 11.875 10.3247 11.8092 10.4419 11.6919C10.5592 11.5747 10.625 11.4158 10.625 11.25V11.1938C12.05 10.932 13.125 9.79531 13.125 8.4375C13.125 6.88672 11.7234 5.625 10 5.625ZM18.125 10C18.125 11.607 17.6485 13.1779 16.7557 14.514C15.8629 15.8502 14.594 16.8916 13.1093 17.5065C11.6247 18.1215 9.99099 18.2824 8.4149 17.9689C6.8388 17.6554 5.39106 16.8815 4.25476 15.7452C3.11846 14.6089 2.34463 13.1612 2.03112 11.5851C1.71762 10.009 1.87852 8.37535 2.49348 6.8907C3.10844 5.40605 4.14985 4.1371 5.486 3.24431C6.82214 2.35152 8.39303 1.875 10 1.875C12.1542 1.87727 14.2195 2.73403 15.7427 4.25727C17.266 5.78051 18.1227 7.84581 18.125 10ZM16.875 10C16.875 8.64025 16.4718 7.31104 15.7164 6.18045C14.9609 5.04987 13.8872 4.16868 12.631 3.64833C11.3747 3.12798 9.99238 2.99183 8.65876 3.2571C7.32514 3.52237 6.10013 4.17716 5.13864 5.13864C4.17716 6.10013 3.52238 7.32513 3.2571 8.65875C2.99183 9.99237 3.12798 11.3747 3.64833 12.6309C4.16868 13.8872 5.04987 14.9609 6.18046 15.7164C7.31105 16.4718 8.64026 16.875 10 16.875C11.8227 16.8729 13.5702 16.1479 14.8591 14.8591C16.1479 13.5702 16.8729 11.8227 16.875 10Z" fill="currentColor"/>
            </svg>
          </button>
          <!-- Settings -->
          <button class="sz-icon-btn" title="Configurações" @click="router.push(`/app/accounts/${accountId}/settings/general`)">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9.9999 6.24966C9.25822 6.24966 8.5332 6.46959 7.91652 6.88165C7.29983 7.2937 6.81919 7.87937 6.53536 8.5646C6.25153 9.24982 6.17727 10.0038 6.32196 10.7312C6.46665 11.4587 6.82381 12.1269 7.34825 12.6513C7.8727 13.1758 8.54089 13.5329 9.26832 13.6776C9.99574 13.8223 10.7497 13.748 11.435 13.4642C12.1202 13.1804 12.7059 12.6997 13.1179 12.083C13.53 11.4664 13.7499 10.7413 13.7499 9.99966C13.7489 9.00541 13.3535 8.05219 12.6504 7.34915C11.9474 6.64611 10.9941 6.25069 9.9999 6.24966ZM9.9999 12.4997C9.50545 12.4997 9.0221 12.353 8.61098 12.0783C8.19986 11.8036 7.87942 11.4132 7.69021 10.9564C7.50099 10.4996 7.45148 9.99689 7.54794 9.51193C7.6444 9.02698 7.88251 8.58152 8.23214 8.23189C8.58177 7.88226 9.02723 7.64416 9.51218 7.5477C9.99713 7.45123 10.4998 7.50074 10.9566 7.68996C11.4134 7.87918 11.8039 8.19961 12.0786 8.61073C12.3533 9.02186 12.4999 9.50521 12.4999 9.99966C12.4999 10.6627 12.2365 11.2986 11.7677 11.7674C11.2988 12.2363 10.6629 12.4997 9.9999 12.4997ZM18.589 8.37544C18.5716 8.28743 18.5354 8.2042 18.483 8.1314C18.4305 8.05861 18.363 7.99795 18.2851 7.95357L15.9546 6.62544L15.9452 3.99888C15.9449 3.90842 15.925 3.8191 15.8868 3.73709C15.8487 3.65508 15.7932 3.58234 15.7241 3.52388C14.8788 2.8088 13.9053 2.2608 12.8554 1.90903C12.7727 1.88105 12.6851 1.87069 12.5981 1.87863C12.5112 1.88657 12.4269 1.91263 12.3507 1.95513L9.9999 3.26919L7.64678 1.95278C7.57049 1.91005 7.48609 1.88379 7.39902 1.87572C7.31195 1.86764 7.22416 1.87793 7.14131 1.90591C6.09221 2.26025 5.11982 2.81033 4.27569 3.527C4.20675 3.58538 4.15129 3.658 4.11313 3.73986C4.07496 3.82173 4.05499 3.9109 4.05459 4.00122L4.04287 6.63013L1.7124 7.95825C1.63444 8.00264 1.56694 8.06329 1.5145 8.13609C1.46206 8.20888 1.42591 8.29212 1.4085 8.38013C1.19521 9.45191 1.19521 10.5552 1.4085 11.627C1.42591 11.715 1.46206 11.7982 1.5145 11.871C1.56694 11.9438 1.63444 12.0045 1.7124 12.0489L4.04287 13.377L4.05225 16.0036C4.05253 16.094 4.07245 16.1833 4.11062 16.2654C4.14879 16.3474 4.20431 16.4201 4.27334 16.4786C5.11871 17.1936 6.09221 17.7416 7.14209 18.0934C7.22477 18.1214 7.31239 18.1318 7.39932 18.1238C7.48624 18.1159 7.57054 18.0898 7.64678 18.0473L9.9999 16.7301L12.353 18.0465C12.4462 18.0984 12.5511 18.1253 12.6577 18.1247C12.726 18.1246 12.7938 18.1136 12.8585 18.0918C13.9075 17.738 14.8799 17.1884 15.7241 16.4723C15.7931 16.4139 15.8485 16.3413 15.8867 16.2595C15.9249 16.1776 15.9448 16.0884 15.9452 15.9981L15.9569 13.3692L18.2874 12.0411C18.3654 11.9967 18.4329 11.936 18.4853 11.8632C18.5378 11.7904 18.5739 11.7072 18.5913 11.6192C18.8034 10.5483 18.8026 9.44606 18.589 8.37544ZM17.4171 11.1028L15.1851 12.3723C15.0873 12.4279 15.0063 12.5089 14.9507 12.6067C14.9054 12.6848 14.8577 12.7676 14.8093 12.8458C14.7473 12.9443 14.7142 13.0583 14.714 13.1747L14.7022 15.6942C14.1023 16.1653 13.4339 16.5421 12.7202 16.8114L10.4687 15.5567C10.3752 15.505 10.27 15.4781 10.1632 15.4786H10.1483C10.0538 15.4786 9.9585 15.4786 9.86397 15.4786C9.75216 15.4758 9.64162 15.5027 9.54365 15.5567L7.29053 16.8145C6.5753 16.5473 5.90508 16.1724 5.30303 15.7028L5.29444 13.1872C5.29405 13.0705 5.26103 12.9563 5.19912 12.8575C5.15069 12.7793 5.10303 12.7012 5.0585 12.6184C5.00328 12.5191 4.92232 12.4365 4.82412 12.3793L2.58975 11.1067C2.47412 10.3753 2.47412 9.63027 2.58975 8.89888L4.81787 7.627C4.91567 7.5714 4.99664 7.49042 5.05225 7.39263C5.09756 7.3145 5.14522 7.23169 5.19365 7.15357C5.25566 7.05505 5.28869 6.94107 5.28897 6.82466L5.30069 4.30513C5.90063 3.83399 6.56901 3.45725 7.28272 3.18794L9.53115 4.44263C9.62901 4.49687 9.73963 4.52385 9.85147 4.52075C9.946 4.52075 10.0413 4.52075 10.1358 4.52075C10.2476 4.52355 10.3582 4.49659 10.4562 4.44263L12.7093 3.18482C13.4245 3.45206 14.0947 3.82696 14.6968 4.29653L14.7054 6.81216C14.7058 6.9288 14.7388 7.043 14.8007 7.14185C14.8491 7.21997 14.8968 7.2981 14.9413 7.38091C14.9965 7.48022 15.0775 7.5628 15.1757 7.61997L17.4101 8.89263C17.5272 9.62459 17.5285 10.3704 17.414 11.1028H17.4171Z" fill="currentColor"/>
            </svg>
          </button>
          <!-- Grid / Products -->
          <div class="sz-products-wrap" style="position:relative;">
            <button class="sz-icon-btn" title="Aplicativos" @click="toggleProductsDropdown">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.5835 7.00065C6.81361 7.00065 7.00016 6.8141 7.00016 6.58398C7.00016 6.35387 6.81361 6.16732 6.5835 6.16732C6.35338 6.16732 6.16683 6.35387 6.16683 6.58398C6.16683 6.8141 6.35338 7.00065 6.5835 7.00065ZM5.3335 6.58398C5.3335 5.89363 5.89314 5.33398 6.5835 5.33398C7.27385 5.33398 7.8335 5.89363 7.8335 6.58398C7.8335 7.27434 7.27385 7.83398 6.5835 7.83398C5.89314 7.83398 5.3335 7.27434 5.3335 6.58398ZM12.0002 7.00065C12.2303 7.00065 12.4168 6.8141 12.4168 6.58398C12.4168 6.35387 12.2303 6.16732 12.0002 6.16732C11.77 6.16732 11.5835 6.35387 11.5835 6.58398C11.5835 6.8141 11.77 7.00065 12.0002 7.00065ZM10.7502 6.58398C10.7502 5.89363 11.3098 5.33398 12.0002 5.33398C12.6905 5.33398 13.2502 5.89363 13.2502 6.58398C13.2502 7.27434 12.6905 7.83398 12.0002 7.83398C11.3098 7.83398 10.7502 7.27434 10.7502 6.58398ZM17.4168 7.00065C17.6469 7.00065 17.8335 6.8141 17.8335 6.58398C17.8335 6.35387 17.6469 6.16732 17.4168 6.16732C17.1867 6.16732 17.0002 6.35387 17.0002 6.58398C17.0002 6.8141 17.1867 7.00065 17.4168 7.00065ZM16.1668 6.58398C16.1668 5.89363 16.7265 5.33398 17.4168 5.33398C18.1072 5.33398 18.6668 5.89363 18.6668 6.58398C18.6668 7.27434 18.1072 7.83398 17.4168 7.83398C16.7265 7.83398 16.1668 7.27434 16.1668 6.58398ZM6.5835 12.4173C6.81361 12.4173 7.00016 12.2308 7.00016 12.0007C7.00016 11.7705 6.81361 11.584 6.5835 11.584C6.35338 11.584 6.16683 11.7705 6.16683 12.0007C6.16683 12.2308 6.35338 12.4173 6.5835 12.4173ZM5.3335 12.0007C5.3335 11.3103 5.89314 10.7507 6.5835 10.7507C7.27385 10.7507 7.8335 11.3103 7.8335 12.0007C7.8335 12.691 7.27385 13.2507 6.5835 13.2507C5.89314 13.2507 5.3335 12.691 5.3335 12.0007ZM12.0002 12.4173C12.2303 12.4173 12.4168 12.2308 12.4168 12.0007C12.4168 11.7705 12.2303 11.584 12.0002 11.584C11.77 11.584 11.5835 11.7705 11.5835 12.0007C11.5835 12.2308 11.77 12.4173 12.0002 12.4173ZM10.7502 12.0007C10.7502 11.3103 11.3098 10.7507 12.0002 10.7507C12.6905 10.7507 13.2502 11.3103 13.2502 12.0007C13.2502 12.691 12.6905 13.2507 12.0002 13.2507C11.3098 13.2507 10.7502 12.691 10.7502 12.0007ZM17.4168 12.4173C17.6469 12.4173 17.8335 12.2308 17.8335 12.0007C17.8335 11.7705 17.6469 11.584 17.4168 11.584C17.1867 11.584 17.0002 11.7705 17.0002 12.0007C17.0002 12.2308 17.1867 12.4173 17.4168 12.4173ZM16.1668 12.0007C16.1668 11.3103 16.7265 10.7507 17.4168 10.7507C18.1072 10.7507 18.6668 11.3103 18.6668 12.0007C18.6668 12.691 18.1072 13.2507 17.4168 13.2507C16.7265 13.2507 16.1668 12.691 16.1668 12.0007ZM6.5835 17.834C6.81361 17.834 7.00016 17.6474 7.00016 17.4173C7.00016 17.1872 6.81361 17.0007 6.5835 17.0007C6.35338 17.0007 6.16683 17.1872 6.16683 17.4173C6.16683 17.6474 6.35338 17.834 6.5835 17.834ZM5.3335 17.4173C5.3335 16.727 5.89314 16.1673 6.5835 16.1673C7.27385 16.1673 7.8335 16.727 7.8335 17.4173C7.8335 18.1077 7.27385 18.6673 6.5835 18.6673C5.89314 18.6673 5.3335 18.1077 5.3335 17.4173ZM12.0002 17.834C12.2303 17.834 12.4168 17.6474 12.4168 17.4173C12.4168 17.1872 12.2303 17.0007 12.0002 17.0007C11.77 17.0007 11.5835 17.1872 11.5835 17.4173C11.5835 17.6474 11.77 17.834 12.0002 17.834ZM10.7502 17.4173C10.7502 16.727 11.3098 16.1673 12.0002 16.1673C12.6905 16.1673 13.2502 16.727 13.2502 17.4173C13.2502 18.1077 12.6905 18.6673 12.0002 18.6673C11.3098 18.6673 10.7502 18.1077 10.7502 17.4173ZM17.4168 17.834C17.6469 17.834 17.8335 17.6474 17.8335 17.4173C17.8335 17.1872 17.6469 17.0007 17.4168 17.0007C17.1867 17.0007 17.0002 17.1872 17.0002 17.4173C17.0002 17.6474 17.1867 17.834 17.4168 17.834ZM16.1668 17.4173C16.1668 16.727 16.7265 16.1673 17.4168 16.1673C18.1072 16.1673 18.6668 16.727 18.6668 17.4173C18.6668 18.1077 18.1072 18.6673 17.4168 18.6673C16.7265 18.6673 16.1668 18.1077 16.1668 17.4173Z" fill="currentColor"/>
              </svg>
            </button>
            <div v-if="showProductsDropdown" class="sz-products-dropdown" @click.stop>
              <div
                v-for="mod in SZ_MODULES"
                :key="mod.name"
                class="sz-product-item"
                :class="{ 'sz-product-active': mod.active }"
              >
                <div class="sz-product-icon">
                  <img :src="mod.icon" :alt="mod.name" style="width:40px;height:40px;border-radius:8px;object-fit:cover;" />
                </div>
                <div class="sz-product-text">
                  <span class="sz-product-name">{{ mod.name }}</span>
                  
                </div>
                <div class="sz-product-status">
                  <svg v-if="mod.active" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#388E3C" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                  <svg v-else width="14" height="14" viewBox="0 0 15 17" fill="#9CA3AF"><path d="M13.75 5.625H11.25V3.75a3.75 3.75 0 00-7.5 0v1.875H1.25C.56 5.625 0 6.185 0 6.875v8.75c0 .69.56 1.25 1.25 1.25h12.5c.69 0 1.25-.56 1.25-1.25v-8.75c0-.69-.56-1.25-1.25-1.25zM5 3.75a2.5 2.5 0 015 0v1.875H5V3.75zm8.75 11.875H1.25V6.875h12.5v8.75z"/></svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Profile pill -->
        <div class="sz-profile-pill" @click="toggleProfileDropdown">
          <div class="sz-profile-avatar">
            <img v-if="userAvatar" :src="userAvatar" :alt="userName" />
            <span v-else class="sz-profile-initials">{{ userInitials }}</span>
          </div>
          <span class="sz-profile-name" :title="userName">{{ userNameShort }}</span>
          <!-- Profile dropdown -->
          <div v-if="showProfileDropdown" class="sz-profile-dropdown" @click.stop>
            <div class="sz-profile-dropdown-header">
              <div class="sz-profile-avatar" style="width:40px;height:40px;">
                <img v-if="userAvatar" :src="userAvatar" :alt="userName" style="width:40px;height:40px;" />
                <span v-else class="sz-profile-initials" style="font-size:14px;">{{ userInitials }}</span>
              </div>
              <div>
                <div style="font-weight:600;font-size:14px;">{{ userName }}</div>
                <div style="font-size:12px;color:#6B7280;">{{ currentUser?.email }}</div>
              </div>
            </div>
            <div class="sz-profile-sep" />
            <div class="sz-profile-menu-item" @click="goToProfile">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span>Configurações do perfil</span>
            </div>
            <div class="sz-profile-menu-item" @click="goToAppearance">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 000 20 14.5 14.5 0 000-20"/><path d="M2 12h20"/></svg>
              <span>Aparência</span>
            </div>
            <div class="sz-profile-menu-item" @click="goToKeyboardShortcuts">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/></svg>
              <span>Atalhos de teclado</span>
            </div>
            <div v-if="isSuperAdmin" class="sz-profile-menu-item" @click="goToSuperAdmin">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <span>Super Admin</span>
            </div>
            <div class="sz-profile-sep" />
            <div class="sz-profile-menu-item sz-logout" @click="doLogout">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              <span>Sair</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-header {
  position: relative;
  width: 100%;
  flex-shrink: 0;
  background: #FFFFFF;
  border-bottom: 1px solid #D4D4D4;
  z-index: 10000;
  isolation: isolate;
  box-sizing: border-box;
  padding: 16px;
  height: 81px;
  font-family: Inter, system-ui, -apple-system, sans-serif;
}
.dark .sz-header {
  background: #1D1E21;
  border-bottom-color: #282A2E;
}

.sz-header-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  height: 49px;
  width: 100%;
}

/* LEFT */
.sz-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.sz-header-logo {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}
.sz-header-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
}
.dark .sz-header-title { color: #fff; }

/* CENTER */
.sz-header-center {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 774px;
  margin: 0 auto;
}
.sz-search-field {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
  flex: 1;
  height: 48px;
  background: #FFFFFF;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.sz-search-field:focus-within {
  border-color: #388E3C;
}
.dark .sz-search-field {
  background: #242528;
  border-color: #303138;
}
.sz-search-icon { flex-shrink: 0; }
.sz-search-input {
  border: none;
  background: transparent;
  font-family: Inter, system-ui, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #000;
  outline: none;
  width: 100%;
}
.sz-search-input::placeholder { color: #D4D4D4; }
.dark .sz-search-input { color: #fff; }

.sz-new-msg-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.1s;
}
.sz-new-msg-btn:hover { background: #F5F5F5; }
.dark .sz-new-msg-btn { background: #282A2E; border-color: #363840; }
.dark .sz-new-msg-btn svg path { stroke: #fff; }

/* RIGHT */
.sz-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Status pill */
.sz-status-pill {
  display: flex;
  align-items: center;
  padding: 0 16px 0 12px;
  gap: 8px;
  height: 48px;
  background: #F5F5F5;
  border-radius: 999px;
  cursor: pointer;
  position: relative;
  transition: background 0.1s;
}
.sz-status-pill:hover { background: #EBEBEB; }
.dark .sz-status-pill { background: #242528; }
.sz-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-status-label {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #000;
  white-space: nowrap;
}
.dark .sz-status-label { color: #fff; }
.sz-status-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  padding: 4px;
  z-index: 99999;
  min-width: 120px;
}
.dark .sz-status-dropdown { background: #282A2E; border-color: #363840; }
.sz-status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
}
.sz-status-option:hover { background: #F5F5F5; }
.dark .sz-status-option { color: #E5E7EB; }
.dark .sz-status-option:hover { background: #363840; }
/* SZ: divider + toggle 'Automático' dentro do status dropdown */
.sz-status-divider { height: 1px; background: #E5E5E5; margin: 6px 4px; }
.dark .sz-status-divider { background: #363840; }
.sz-status-auto-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
}
.sz-status-auto-toprow {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sz-status-check { color: #111827; flex-shrink: 0; margin-left: auto; }
.dark .sz-status-check { color: #E5E7EB; }
.sz-status-auto-row:hover { background: #F5F5F5; }
.dark .sz-status-auto-row:hover { background: #363840; }
.sz-status-auto-label { font-size: 13px; font-weight: 500; color: #374151; line-height: 16px; }
.sz-status-auto-help { font-size: 11px; color: #6B7280; line-height: 14px; display: block; width: 100%; }
.dark .sz-status-auto-label { color: #E5E7EB; }
.dark .sz-status-auto-help { color: #9CA3AF; }
.sz-status-auto-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
}
.sz-status-auto-switch input { opacity: 0; width: 0; height: 0; }
.sz-status-auto-slider {
  position: absolute;
  inset: 0;
  background: #D1D5DB;
  border-radius: 999px;
  transition: background 0.15s;
}
.sz-status-auto-slider::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 2px;
  top: 2px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.15s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.sz-status-auto-switch.is-on .sz-status-auto-slider { background: #388E3C; }
.sz-status-auto-switch.is-on .sz-status-auto-slider::before { transform: translateX(14px); }
.dark .sz-status-auto-slider { background: #4B5563; }


/* Icon group */
.sz-icon-group {
  display: flex;
  align-items: center;
  gap: 4px;
}
.sz-icon-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  background: #FFFFFF;
  color: #000000;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 4px;
  transition: background 0.1s;
}
.sz-icon-btn:hover { background: #F5F5F5; }
.dark .sz-icon-btn { background: transparent; color: #E5E7EB; }
.sz-ia-lottie-wrap { position: relative; width: 38px; height: 38px; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; border-radius: 8px; overflow: hidden; }
.sz-ia-lottie-wrap:hover { opacity: 0.85; }
.sz-ia-lottie-layer { position: absolute; inset: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; transition: opacity 80ms linear; pointer-events: none; }
.sz-ia-lottie-layer svg { width: 38px !important; height: 38px !important; }
.dark .sz-icon-btn:hover { background: #363840; }
.dark .sz-icon-btn svg path,
.dark .sz-icon-btn svg circle { stroke: #D1D5DB; fill: none; }
.dark .sz-icon-btn svg circle[fill="#000"] { fill: #D1D5DB; stroke: none; }

/* Profile pill */
.sz-profile-pill {
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
  height: 49px;
  border: 1px solid #D4D4D4;
  border-radius: 999px;
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s;
}
.sz-profile-pill:hover {
  border-color: #388E3C;
}
.dark .sz-profile-pill { border-color: #4B5563; }
.sz-profile-avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid #F5F5F5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #388E3C;
  flex-shrink: 0;
}
.sz-profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 999px;
}
.sz-profile-initials {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
}
.sz-profile-name {
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #000;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark .sz-profile-name { color: #fff; }

/* Products dropdown */
.sz-products-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 260px;
  background: #fff;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  padding: 8px 0;
  z-index: 99999;
  animation: szDropFade 0.15s ease;
}
.dark .sz-products-dropdown { background: #282A2E; border-color: #363840; }
@keyframes szDropFade { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
.sz-product-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; margin: 2px 8px;
  border-radius: 8px; cursor: pointer; transition: background 0.1s;
}
.sz-product-item:hover { background: #F5F5F5; }
.dark .sz-product-item:hover { background: #363840; }
.sz-product-active { background: rgba(56,142,60,0.08); }
.sz-product-active:hover { background: rgba(56,142,60,0.15); }
.sz-product-meta {
  font-size: 12px;
  color: #9CA3AF;
  line-height: 1.3;
}
.dark .sz-product-meta { color: #6B7280; }
.sz-product-icon {
  width: 40px; height: 40px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden;
}
.sz-product-icon img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sz-product-name { font-size: 14px; font-weight: 600; color: #1A1A1A; }
.dark .sz-product-name { color: #F3F4F6; }
.sz-product-text { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.sz-product-status { flex-shrink: 0; display: flex; align-items: center; }

/* Profile dropdown */
.sz-profile-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 260px;
  background: #fff;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  padding: 8px 0;
  z-index: 99999;
  animation: szDropFade 0.15s ease;
}
.dark .sz-profile-dropdown { background: #282A2E; border-color: #363840; }
.sz-profile-dropdown-header {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px;
}
.sz-profile-sep { height: 1px; background: #E5E5E5; margin: 4px 8px; }
.dark .sz-profile-sep { background: #363840; }
.sz-profile-menu-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 16px; cursor: pointer; font-size: 13px;
  color: #374151; transition: background 0.1s;
}
.sz-profile-menu-item:hover { background: #F5F5F5; }
.dark .sz-profile-menu-item { color: #E5E7EB; }
.dark .sz-profile-menu-item:hover { background: #363840; }
.sz-profile-menu-item.sz-logout { color: #DC2626; }
.sz-profile-menu-item.sz-logout:hover { background: #FEF2F2; }
.dark .sz-profile-menu-item.sz-logout:hover { background: rgba(220,38,38,0.1); }

/* Dark mode - complete coverage */
.dark .sz-header-logo svg rect { fill: #388E3C; }
.dark .sz-search-field:focus-within { border-color: #4ADE80; }
.dark .sz-search-input::placeholder { color: #6B7280; }
.dark .sz-new-msg-btn:hover { background: #363840; }
.dark .sz-status-pill:hover { background: #363840; }
.dark .sz-status-dropdown .sz-status-dot { border: 1px solid #363840; }
.dark .sz-profile-pill { border-color: #4B5563; }
.dark .sz-profile-pill:hover { border-color: #4ADE80; }
.dark .sz-profile-initials { color: #fff; }
.dark .sz-profile-dropdown-header div { color: #E5E7EB; }
.dark .sz-profile-dropdown-header div:last-child { color: #9CA3AF; }
.dark .sz-product-active { background: rgba(56,142,60,0.15); }
.dark .sz-product-active:hover { background: rgba(56,142,60,0.25); }
.dark .sz-profile-menu-item svg { stroke: #D1D5DB; }
.dark .sz-profile-menu-item.sz-logout svg { stroke: #F87171; }
.dark .sz-profile-menu-item.sz-logout { color: #F87171; }

/* Search dropdown */
.sz-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  z-index: 99999;
  max-height: 420px;
  overflow-y: auto;
  animation: szDropFade 0.15s ease;
}
.dark .sz-search-dropdown { background: #242528; border-color: #303138; }
.sz-search-loading {
  padding: 16px;
  text-align: center;
  color: #9CA3AF;
  font-size: 13px;
}
.sz-search-empty {
  padding: 20px 16px;
  text-align: center;
  color: #9CA3AF;
  font-size: 13px;
}
.sz-search-section {
  padding: 4px 0;
}
.sz-search-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #9CA3AF;
  letter-spacing: 0.05em;
}
.sz-search-clear {
  border: none;
  background: none;
  color: #388E3C;
  font-size: 11px;
  cursor: pointer;
  font-weight: 500;
}
.sz-search-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.1s;
}
.sz-search-item:hover { background: #F5F5F5; }
.dark .sz-search-item:hover { background: #363840; }
.sz-search-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
}
.dark .sz-search-avatar { background: #4B5563; color: #D1D5DB; }
.sz-search-avatar img { width: 100%; height: 100%; object-fit: cover; }
.sz-search-item-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.sz-search-item-name {
  font-size: 13px;
  font-weight: 500;
  color: #1F2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* SZ: badge cinza da última atividade — alinhado ao lado do inbox tag */
.sz-search-item-date {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #E5E7EB;
  color: #4B5563;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 4px;
}
.dark .sz-search-item-name { color: #E5E7EB; }
.dark .sz-search-item-date {
  background: #3D3F43;
  color: #BFBFC5;
}
.sz-search-item-meta {
  font-size: 11px;
  color: #9CA3AF;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sz-search-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-top: 1px solid #E5E5E5;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #388E3C;
  transition: background 0.1s;
}
.sz-search-footer:hover { background: #F5F5F5; }
.dark .sz-search-footer { border-color: #363840; }
.dark .sz-search-footer:hover { background: #363840; }

.sz-search-conv-id {
  font-size: 11px;
  color: #9CA3AF;
  flex-shrink: 0;
  font-weight: 500;
}

.sz-search-inbox-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  background: #E8F5E9;
  color: #2E7D32;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  margin-right: 4px;
  white-space: nowrap;
}
.dark .sz-search-inbox-tag {
  background: rgba(56,142,60,0.15);
  color: #4ADE80;
}

/* Compose new conversation modal */
.sz-compose-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.sz-compose-modal {
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  background: var(--color-background, #fff);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.dark .sz-compose-modal {
  background: #1F2125;
}
.sz-compose-modal :deep(.absolute) {
  position: relative !important;
}
.sz-compose-modal :deep(.w-full.h-full) {
  height: auto !important;
  max-height: 80vh;
}

/* Complete dark mode for topbar */
.dark .sz-search-input::placeholder { color: #6B7280; }
.dark .sz-search-dropdown { background: #242528; border-color: #303138; }
.dark .sz-search-section-header { color: #9CA3AF; border-color: #363840; }
.dark .sz-search-item { color: #E5E7EB; }
.dark .sz-search-item:hover { background: #363840; }
.dark .sz-search-item-name { color: #E5E7EB; }
.dark .sz-search-item-meta { color: #6B7280; }
.dark .sz-search-empty { color: #6B7280; }
.dark .sz-search-footer { border-color: #363840; color: #4ADE80; }
.dark .sz-search-footer:hover { background: #363840; }
.dark .sz-profile-avatar { background: #363840; }
.dark .sz-profile-initials { color: #E5E7EB; }
.dark .sz-profile-dropdown { background: #282A2E; border-color: #363840; }
.dark .sz-profile-sep { background: #363840; }
.dark .sz-profile-menu-item { color: #E5E7EB; }
.dark .sz-profile-menu-item:hover { background: #363840; }
.dark .sz-profile-menu-item svg { color: #9CA3AF; }
.dark .sz-logout { color: #F87171; }
.dark .sz-product-name { color: #E5E7EB; }
.dark .sz-product-meta { color: #6B7280; }
.dark .sz-product-status svg { color: #6B7280; }
.dark .sz-icon-btn svg path[fill="currentColor"],
.dark .sz-icon-btn svg path[fill-rule] { fill: #D1D5DB; }
.dark .sz-search-icon path { stroke: #6B7280; }
.dark .sz-status-dropdown { box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
.dark .sz-products-dropdown { box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
.dark .sz-profile-dropdown { box-shadow: 0 8px 24px rgba(0,0,0,0.4); }

/* Search filter chips */
.sz-search-filters-bar {
  display: flex;
  gap: 4px;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #E5E7EB;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.sz-search-filters-bar::-webkit-scrollbar { display: none; }
.dark .sz-search-filters-bar { border-bottom-color: #363840; }
.sz-search-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 24px;
  padding: 0 6px 0 3px;
  border: 1px solid #E5E5E5;
  border-radius: 999px;
  background: #FFFFFF;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
  user-select: none;
  box-sizing: border-box;
  flex: 0 0 auto;
  font-family: inherit;
}
.sz-search-filter-chip:hover { background: rgba(56,142,60,0.06); }
.sz-search-filter-chip.active {
  background: #DFEFE3;
  border-color: transparent;
}
.sz-search-filter-chip.active:hover { background: #D2E7D6; }
.sz-search-filter-chip:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}
.sz-search-filter-chip-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: 1px solid #B5B5B5;
  border-radius: 999px;
  background: transparent;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}
.sz-search-filter-chip-check--on {
  background: #59AA59;
  border-color: #59AA59;
}
.sz-search-filter-chip-label {
  font-family: inherit;
  font-size: 11px;
  font-weight: 500;
  line-height: 20px;
  color: #000000;
}
.dark .sz-search-filter-chip { background: transparent; border-color: #3D3F43; }
.dark .sz-search-filter-chip:hover { background: rgba(255,255,255,0.04); }
.dark .sz-search-filter-chip.active { background: #3D3F43; border-color: transparent; }
.dark .sz-search-filter-chip.active:hover { background: #4A4D52; }
.dark .sz-search-filter-chip-label { color: #9797A0; }
.dark .sz-search-filter-chip.active .sz-search-filter-chip-label { color: #FFFFFF; }
.dark .sz-search-filter-chip-check { background: transparent; border-color: #3D3F43; }
.dark .sz-search-filter-chip-check--on { background: #59AA59; border-color: #59AA59; }
@media (max-width: 640px) {
  .sz-search-filters-bar { padding: 8px 10px 6px; gap: 4px; }
}

</style>
